import { useEffect, useState, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Spinner } from '@/components/ui/spinner';
import { Modal } from '@/components/ui/modal';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  FileText,
  Building2,
  CreditCard,
  SlidersHorizontal,
  Menu,
  X,
  Search,
  Pause,
  Play,
  DollarSign,
  Users,
  ClipboardList,
  AlertTriangle,
  UserPlus,
  LayoutDashboard,
} from 'lucide-react';
import { adminApi, AdminDashboardSummary, AdminSubscription } from '../services/adminApi';
import { PlatformTopBarWrapper } from '../components/PlatformTopBarWrapper';
import OrganizationsTab from './OrganizationsTab';
import AdminBillingTab from './AdminBillingTab';
import RatePlansTab from './RatePlansTab';
import SignupsTab from './SignupsTab';
import {
  type AdminTab,
  getAdminDashboardUrl,
  resolveAdminTabFromSearch,
} from './adminNavigation';
import './AdminDashboard.css';

// Status color semantics preserved from the Fluent Badge mapping:
// success/emerald, warning/amber, destructive/red, neutral/gray.
const ON_EMERALD = 'border-transparent bg-gold/15 text-foreground';
const ON_AMBER = 'border-transparent bg-warning/10 text-warning';
const ON_RED = 'border-transparent bg-destructive/10 text-destructive';
const NEUTRAL_GRAY = 'border-transparent bg-muted text-text-secondary';

interface NavItem {
  id: AdminTab;
  label: string;
  icon: React.ReactNode;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

const NAV_SECTIONS: NavSection[] = [
  {
    title: 'CUSTOMERS',
    items: [
      { id: 'organizations', label: 'Organizations', icon: <Building2 size={18} /> },
    ],
  },
  {
    title: 'BILLING',
    items: [
      { id: 'revenue', label: 'Revenue', icon: <CreditCard size={18} /> },
      { id: 'rate-plans', label: 'Rate Plans', icon: <SlidersHorizontal size={18} /> },
    ],
  },
  {
    title: 'ADVANCED',
    items: [
      { id: 'subscriptions', label: 'Marketplace Subs', icon: <FileText size={18} /> },
    ],
  },
];

const REFRESH_INTERVAL_MS = 60_000; // 1 minute

function AdminDashboard() {
  // Auth is guaranteed by ProtectedRoute + StaffGate (U2); the top bar owns
  // identity/org/logout via PlatformTopBarWrapper (U4).
  const [activeTab, setActiveTab] = useState<AdminTab>(() =>
    resolveAdminTabFromSearch(window.location.search)
  );
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Platform-analyst probe (PAR-7354). Gates the Signups nav entry — hidden
  // unless the current staffer can view signups. Fails closed on any error.
  const [isPlatformAnalyst, setIsPlatformAnalyst] = useState(false);
  useEffect(() => {
    let cancelled = false;
    adminApi
      .getMyPlatformAnalyst()
      .then((res) => {
        if (!cancelled) setIsPlatformAnalyst(Boolean(res.isPlatformAnalyst));
      })
      .catch(() => {
        if (!cancelled) setIsPlatformAnalyst(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Inject the Signups entry into CUSTOMERS (directly under Organizations) only
  // for platform analysts; every other section is untouched.
  const navSections: NavSection[] = NAV_SECTIONS.map((section) => {
    if (section.title === 'CUSTOMERS' && isPlatformAnalyst) {
      return {
        ...section,
        items: [
          ...section.items,
          { id: 'signups', label: 'Signups', icon: <UserPlus size={18} /> },
        ],
      };
    }
    return section;
  });

  const handleNavClick = (tabId: AdminTab) => {
    setActiveTab(tabId);
    window.history.replaceState({}, '', getAdminDashboardUrl(tabId));
    setSidebarOpen(false); // Close mobile sidebar on selection
  };

  const [summary, setSummary] = useState<AdminDashboardSummary | null>(null);
  const [subscriptions, setSubscriptions] = useState<AdminSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [continuationToken, setContinuationToken] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<AdminSubscription | null>(null);
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');

  const fetchSummary = useCallback(async () => {
    try {
      const data = await adminApi.getSummary();
      setSummary(data);
      setError(null);
    } catch (err: unknown) {
      console.error('Failed to fetch summary:', err);
      let errorMsg = 'Failed to fetch summary';
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosErr = err as { response?: { data?: { error?: string; details?: string }; status?: number } };
        if (axiosErr.response?.data?.details) {
          errorMsg = `${axiosErr.response.data.error}: ${axiosErr.response.data.details}`;
        } else if (axiosErr.response?.data?.error) {
          errorMsg = axiosErr.response.data.error;
        } else if (axiosErr.response?.status) {
          errorMsg = `Request failed with status ${axiosErr.response.status}`;
        }
      } else if (err instanceof Error) {
        errorMsg = err.message;
      }
      setError(errorMsg);
    }
  }, []);

  const fetchSubscriptions = useCallback(async (reset: boolean = false) => {
    try {
      setLoading(true);
      const response = await adminApi.listSubscriptions(
        statusFilter || undefined,
        searchTerm || undefined,
        50,
        reset ? undefined : continuationToken || undefined
      );

      if (reset) {
        setSubscriptions(response.subscriptions);
      } else {
        setSubscriptions(prev => [...prev, ...response.subscriptions]);
      }
      setContinuationToken(response.continuationToken);
      setHasMore(response.hasMore);
      setError(null);
    } catch (err: unknown) {
      let errorMsg = 'Failed to fetch subscriptions';
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosErr = err as { response?: { data?: { error?: string; details?: string }; status?: number } };
        if (axiosErr.response?.data?.details) {
          errorMsg = `${axiosErr.response.data.error}: ${axiosErr.response.data.details}`;
        } else if (axiosErr.response?.data?.error) {
          errorMsg = axiosErr.response.data.error;
        } else if (axiosErr.response?.status) {
          errorMsg = `Request failed with status ${axiosErr.response.status}`;
        }
      } else if (err instanceof Error) {
        errorMsg = err.message;
      }
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchTerm, continuationToken]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [summaryData, subsData] = await Promise.all([
          adminApi.getSummary(),
          adminApi.listSubscriptions(statusFilter || undefined, searchTerm || undefined, 50),
        ]);
        setSummary(summaryData);
        setSubscriptions(subsData.subscriptions);
        setContinuationToken(subsData.continuationToken);
        setHasMore(subsData.hasMore);
        setError(null);
      } catch (err: unknown) {
        let errorMsg = 'Failed to load data';
        if (err instanceof Error) errorMsg = err.message;
        setError(errorMsg);
      } finally {
        setLoading(false);
      }
    };
    loadData();

    const intervalId = setInterval(async () => {
      try {
        const data = await adminApi.getSummary();
        setSummary(data);
      } catch (err) {
        console.error('Failed to refresh summary:', err);
      }
    }, REFRESH_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [statusFilter, searchTerm]);

  const handleRefresh = () => {
    fetchSummary();
    fetchSubscriptions(true);
  };

  const handleSuspend = async () => {
    if (!selectedSubscription || !suspendReason) return;

    try {
      await adminApi.suspendSubscription(selectedSubscription.id, suspendReason);
      setSuspendDialogOpen(false);
      setSuspendReason('');
      handleRefresh();
    } catch (err) {
      console.error('Failed to suspend subscription:', err);
    }
  };

  const handleReinstate = async (subscription: AdminSubscription) => {

    try {
      await adminApi.reinstateSubscription(subscription.id);
      handleRefresh();
    } catch (err) {
      console.error('Failed to reinstate subscription:', err);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const getStatusBadge = (status: string) => {
    const cls: Record<string, string> = {
      Subscribed: ON_EMERALD,
      PendingFulfillment: ON_AMBER,
      Suspended: ON_RED,
      Unsubscribed: NEUTRAL_GRAY,
    };
    return <Badge variant="outline" className={cls[status] || NEUTRAL_GRAY}>{status}</Badge>;
  };

  // Render the shared platform top bar (U4). AdminDashboard and all its tabs are
  // Fluent-free now, so this is a plain padded wrapper (no app-level FluentProvider
  // on this route — the shadcn primitives are themed by the global CSS tokens).
  const renderWithTopBar = (content: React.ReactNode) => (
    <>
      <PlatformTopBarWrapper />
      <div style={{ paddingTop: 'var(--topbar-height)', minHeight: '100vh' }}>
        {content}
      </div>
    </>
  );

  // Auth loading and the unauthenticated sign-in surface are handled by
  // ProtectedRoute + StaffGate at the router (U2), so no in-component auth gate
  // is needed here.

  // Sidebar component
  const sidebar = (
    <aside className={`admin-sidebar ${sidebarOpen ? 'admin-sidebar--open' : ''}`}>
      <div className="admin-sidebar-header">
        <span className="admin-sidebar-title">Admin</span>
        <button
          className="admin-sidebar-close"
          onClick={() => setSidebarOpen(false)}
          aria-label="Close sidebar"
        >
          <X size={20} />
        </button>
      </div>
      <nav className="admin-sidebar-nav">
        {navSections.map((section) => (
          <div key={section.title} className="admin-nav-section">
            <div className="admin-nav-section-title">{section.title}</div>
            {section.items.map((item) => (
              <button
                key={item.id}
                className={`admin-nav-item ${activeTab === item.id ? 'admin-nav-item--active' : ''}`}
                onClick={() => handleNavClick(item.id)}
                aria-current={activeTab === item.id ? 'page' : undefined}
              >
                <span className="admin-nav-item-icon">{item.icon}</span>
                <span className="admin-nav-item-label">{item.label}</span>
              </button>
            ))}
          </div>
        ))}
        <div className="admin-nav-section">
          <div className="admin-nav-section-title">ORGANIZATION</div>
          <a href="/overview" className="admin-nav-item">
            <span className="admin-nav-item-icon"><LayoutDashboard size={18} /></span>
            <span className="admin-nav-item-label">AI Employee Dashboard</span>
          </a>
        </div>
      </nav>
    </aside>
  );

  return renderWithTopBar(
    <div className="admin-layout">
      {/* Mobile sidebar toggle */}
      <button
        className="admin-sidebar-toggle"
        onClick={() => setSidebarOpen(true)}
        aria-label="Open sidebar navigation"
      >
        <Menu size={20} />
      </button>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="admin-sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {sidebar}

      <main className="admin-content">
        {error && activeTab === 'subscriptions' && (
          <div className="admin-error">
            <AlertTriangle className="h-5 w-5" />
            <span>{error}</span>
          </div>
        )}

        {/* Organizations Tab Content */}
        {activeTab === 'organizations' && (
          <OrganizationsTab />
        )}

        {/* Signups Tab Content */}
        {activeTab === 'signups' && (
          <SignupsTab />
        )}

        {/* Revenue Tab Content */}
        {activeTab === 'revenue' && (
          <AdminBillingTab />
        )}

        {/* Rate Plans Tab Content */}
        {activeTab === 'rate-plans' && (
          <RatePlansTab />
        )}

        {/* Subscriptions Tab Content */}
        {activeTab === 'subscriptions' && loading && subscriptions.length === 0 && (
          <div className="admin-loading">
            <Spinner size={32} label="Loading subscriptions..." />
          </div>
        )}
        {activeTab === 'subscriptions' && summary && (
          <div className="summary-grid">
            <Card className="summary-card">
              <h3>Monthly Recurring Revenue</h3>
              <span className="block" style={{ color: 'var(--admin-text-dim)', fontSize: '0.8rem', marginTop: '0.15rem' }}>
                Active subscriptions only
              </span>
              <div className="summary-value mrr">
                <DollarSign />
                <span>{formatCurrency(summary.monthlyRecurringRevenue)}</span>
              </div>
            </Card>

            <Card className="summary-card">
              <h3>Total Seats</h3>
              <span className="block" style={{ color: 'var(--admin-text-dim)', fontSize: '0.8rem', marginTop: '0.15rem' }}>
                AIE seats across all active subscriptions
              </span>
              <div className="summary-value">
                <Users />
                <span>{summary.totalSeats}</span>
              </div>
            </Card>

            <Card className="summary-card">
              <h3>Subscriptions</h3>
              <span className="block" style={{ color: 'var(--admin-text-dim)', fontSize: '0.8rem', marginTop: '0.15rem' }}>
                Active / Total
              </span>
              <div className="summary-value">
                <ClipboardList />
                <span>{summary.activeSubscriptions} / {summary.totalSubscriptions}</span>
              </div>
            </Card>

            <Card className="summary-card status-breakdown">
              <h3>Status Breakdown</h3>
              <div className="status-list">
                <div className="status-item">
                  <Badge variant="outline" className={ON_EMERALD}>Active</Badge>
                  <span>{summary.activeSubscriptions}</span>
                </div>
                <div className="status-item">
                  <Badge variant="outline" className={ON_AMBER}>Pending</Badge>
                  <span>{summary.pendingSubscriptions}</span>
                </div>
                <div className="status-item">
                  <Badge variant="outline" className={ON_RED}>Suspended</Badge>
                  <span>{summary.suspendedSubscriptions}</span>
                </div>
                <div className="status-item">
                  <Badge variant="outline" className={NEUTRAL_GRAY}>Cancelled</Badge>
                  <span>{summary.cancelledSubscriptions}</span>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Subscriptions Table */}
        {activeTab === 'subscriptions' && (
        <div className="subscriptions-section">
          <h2>Subscriptions</h2>

          <div className="filters-row">
            <div className="search-input relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by email, org ID, or subscription ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select
              value={statusFilter || 'all'}
              onValueChange={(value) => setStatusFilter(value === 'all' ? '' : value)}
            >
              <SelectTrigger aria-label="Filter by status" className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="Subscribed">Active</SelectItem>
                <SelectItem value="PendingFulfillment">Pending</SelectItem>
                <SelectItem value="Suspended">Suspended</SelectItem>
                <SelectItem value="Unsubscribed">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div style={{ overflowX: 'auto' }}>
          <table className="admin-table">
            <thead>
              <tr>
                <th style={{ width: '18%' }}>Email</th>
                <th style={{ width: '16%' }}>Organization</th>
                <th style={{ width: '12%' }}>Plan</th>
                <th style={{ width: '6%' }}>Seats</th>
                <th style={{ width: '12%' }}>Status</th>
                <th style={{ width: '10%' }}>MRR</th>
                <th style={{ width: '10%' }}>Created</th>
                <th style={{ width: '16%' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {subscriptions.map(sub => (
                <tr
                  key={sub.id}
                  className="clickable-row"
                  onClick={() => { window.location.href = `/admin/subscriptions/${sub.id}`; }}
                >
                  <td title={sub.beneficiaryEmail || ''}>{sub.beneficiaryEmail || '-'}</td>
                  <td title={sub.organizationId || ''}>{sub.organizationId || 'Not activated'}</td>
                  <td>{sub.planId}</td>
                  <td>{sub.quantity}</td>
                  <td>{getStatusBadge(sub.status)}</td>
                  <td>{formatCurrency(sub.monthlyRevenue)}</td>
                  <td>{formatDate(sub.createdAt)}</td>
                  <td className="actions-cell" onClick={e => e.stopPropagation()}>
                    <div className="action-buttons">
                      {sub.status === 'Subscribed' && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          title="Suspend"
                          onClick={() => {
                            setSelectedSubscription(sub);
                            setSuspendDialogOpen(true);
                          }}
                        >
                          <Pause />
                        </Button>
                      )}
                      {sub.status === 'Suspended' && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          title="Reinstate"
                          onClick={() => handleReinstate(sub)}
                        >
                          <Play />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>

          {hasMore && (
            <Button
              onClick={() => fetchSubscriptions(false)}
              disabled={loading}
              className="load-more-btn"
            >
              {loading ? <Spinner size={16} /> : 'Load More'}
            </Button>
          )}
        </div>
        )}

        {/* Suspend Dialog */}
        <Modal
          open={suspendDialogOpen}
          onOverlayClick={() => setSuspendDialogOpen(false)}
        >
          <Modal.Content maxWidth="md" aria-labelledby="suspend-subscription-title">
            <Modal.Header className="px-6 py-4">
              <h2 id="suspend-subscription-title" className="text-lg font-semibold text-foreground">
                Suspend Subscription
              </h2>
            </Modal.Header>
            <Modal.Body className="px-6 py-4">
              <span className="block text-sm">
                Are you sure you want to suspend this subscription? The customer's AIEs will be disabled.
              </span>
              <Textarea
                value={suspendReason}
                onChange={(e) => setSuspendReason(e.target.value)}
                placeholder="Reason for suspension..."
                rows={3}
                style={{ width: '100%', marginTop: '1rem' }}
                required
              />
            </Modal.Body>
            <Modal.Footer className="flex justify-end gap-2 px-6 py-4">
              <Button variant="secondary" onClick={() => setSuspendDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSuspend} disabled={!suspendReason}>
                Suspend
              </Button>
            </Modal.Footer>
          </Modal.Content>
        </Modal>
      </main>
    </div>
  );
}

export default AdminDashboard;
